import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPatch, apiPost, ApiError } from '../lib/apiClient';
import { DataTable } from '../components/DataTable';

/**
 * The moderation review queue (docs/administration.md §24.8).
 *
 * F1 shipped this against promotions alone. F2-F4 broadened it, and the screen now
 * reflects the one distinction that matters in the policy — **the two gates work in
 * opposite directions**:
 *
 * - **Review** (pre-publication: promotions, protocols) — content is invisible until
 *   approved. The backlog here is real work: nothing reaches a patient until someone
 *   acts, so the oldest-waiting age is shown prominently. A review gate with no
 *   visible backlog becomes an indefinite hold, which is worse for the author than
 *   no gate at all.
 * - **Published** (post-publication: Pulse posts, specialist profiles) — content is
 *   already live. There is no backlog and no pending state; the action is takedown,
 *   and a moderator finds material by browsing or from the automated upload flags.
 *
 * Presenting these as one undifferentiated list would imply a backlog where there is
 * none, and hide the real one under the entire Pulse feed.
 */
type ModerationStatus = 'pending' | 'approved' | 'rejected';
type ArtifactType =
  | 'promotion'
  | 'protocol'
  | 'pulse_post'
  | 'specialist_profile'
  | 'service_card'
  | 'featured_ad';
type Tab = 'review' | 'published' | 'flags';

interface QueueItem {
  id: string;
  artifactType: ArtifactType;
  artifactLabel?: string;
  title?: string;
  name?: string;
  content?: string;
  specialistName?: string;
  specialistId?: string;
  specialtyLabel?: string;
  /** Featured ads carry their patient-facing copy and media here. */
  bannerConfig?: {
    title?: string;
    description?: string;
    bannerImageUrl?: string | null;
    bannerVideoUrl?: string | null;
    serviceType?: string;
    pricing?: { originalAmount?: number; finalAmount?: number; discountLabel?: string | null };
  };
  createdAt?: string;
  /** Echoed back on a decision so the server can refuse one on a newer version. */
  updatedAt?: string;
  moderationStatus?: ModerationStatus;
  moderationReason?: string;
  takenDown?: boolean;
}

interface FlagItem {
  id: string;
  reasons?: string[];
  userId?: string;
  filePath?: string;
  createdAt?: string;
}

const STATUSES: ModerationStatus[] = ['pending', 'approved', 'rejected'];
/** Post-publication types, which the backend refuses to serve from the queue. */
const PUBLISHED_TYPES: ArtifactType[] = ['pulse_post', 'specialist_profile'];

/** Whole days since `iso`, or null when it is missing or unparseable. */
export function ageInDays(iso: string | undefined, now: number = Date.now()): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((now - then) / 86_400_000);
}

/**
 * The backlog headline: how long the oldest waiting item has waited.
 *
 * Returns null for an empty queue rather than 0 — "0 days" on an empty queue reads
 * as a fresh backlog rather than no backlog.
 */
export function oldestPendingAgeDays(
  items: Array<Pick<QueueItem, 'createdAt'>>,
  now: number = Date.now(),
): number | null {
  const ages = items.map((i) => ageInDays(i.createdAt, now)).filter((a): a is number => a !== null);
  return ages.length ? Math.max(...ages) : null;
}

/**
 * A best-effort human label for a row.
 *
 * Each artifact names itself differently — promotions have `title`, protocols `name`,
 * Pulse posts only their body text. Falls back to the id rather than rendering an
 * empty cell, so a row is always identifiable and actionable.
 */
export function itemTitle(item: QueueItem): string {
  const raw = item.title ?? item.bannerConfig?.title ?? item.name ?? item.content ?? '';
  const trimmed = raw.trim();
  if (!trimmed) return item.id;
  return trimmed.length > 80 ? `${trimmed.slice(0, 77)}…` : trimmed;
}

/**
 * What a reviewer must see before approving a featured ad: the banner media and the
 * copy patients will read. Null for every other artifact type.
 */
export function adPreview(item: QueueItem): {
  imageUrl: string | null;
  videoUrl: string | null;
  description: string | null;
  serviceType: string | null;
  /** The price claim patients will read, e.g. "$50 → $35 (30% OFF)". */
  price: string | null;
} | null {
  if (item.artifactType !== 'featured_ad' || !item.bannerConfig) return null;
  const bc = item.bannerConfig;
  return {
    imageUrl: bc.bannerImageUrl ?? null,
    videoUrl: bc.bannerVideoUrl ?? null,
    description: bc.description ?? null,
    serviceType: bc.serviceType ?? null,
    price: formatAdPrice(bc.pricing),
  };
}

function formatAdPrice(p: NonNullable<QueueItem['bannerConfig']>['pricing']): string | null {
  if (!p || typeof p.finalAmount !== 'number') return null;
  const final = `$${p.finalAmount}`;
  const base =
    typeof p.originalAmount === 'number' && p.originalAmount > p.finalAmount
      ? `$${p.originalAmount} → ${final}`
      : final;
  return p.discountLabel ? `${base} (${p.discountLabel})` : base;
}

/**
 * The PATCH body for a decision. Carries the `updatedAt` the moderator reviewed, so the
 * server refuses (409) an approval of a version edited after the queue loaded.
 */
export function decisionBody(
  item: QueueItem,
  decision: 'approved' | 'rejected',
  why?: string,
): { decision: 'approved' | 'rejected'; reason?: string; expectedUpdatedAt?: string } {
  return {
    decision,
    ...(why ? { reason: why } : {}),
    ...(item.updatedAt ? { expectedUpdatedAt: item.updatedAt } : {}),
  };
}

export function ModerationQueuePage() {
  const [tab, setTab] = useState<Tab>('review');
  const [status, setStatus] = useState<ModerationStatus>('pending');
  const [publishedType, setPublishedType] = useState<ArtifactType>('pulse_post');

  const [items, setItems] = useState<QueueItem[]>([]);
  const [flags, setFlags] = useState<FlagItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'flags') {
        const data = await apiGet<{ items: FlagItem[] }>('/moderation/flags');
        setFlags(data.items ?? []);
        setItems([]);
      } else if (tab === 'published') {
        const data = await apiGet<{ items: QueueItem[] }>(
          `/moderation/browse/${encodeURIComponent(publishedType)}`,
        );
        setItems(data.items ?? []);
      } else {
        const data = await apiGet<{ items: QueueItem[] }>(
          `/moderation/queue/all?status=${encodeURIComponent(status)}`,
        );
        setItems(data.items ?? []);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load the moderation queue');
    } finally {
      setLoading(false);
    }
  }, [tab, status, publishedType]);

  useEffect(() => {
    void load();
  }, [load]);

  // Only the review tab has a backlog worth reporting. Post-publication content has
  // no pending state, so an age there would be the age of the feed, not of work.
  const backlogDays = useMemo(
    () => (tab === 'review' && status === 'pending' ? oldestPendingAgeDays(items) : null),
    [items, tab, status],
  );

  const decide = async (item: QueueItem, decision: 'approved' | 'rejected', why?: string) => {
    setBusyId(item.id);
    setError(null);
    try {
      await apiPatch(
        `/moderation/${encodeURIComponent(item.artifactType)}/${encodeURIComponent(item.id)}`,
        decisionBody(item, decision, why),
      );
      setItems((prev) => prev.filter((p) => p.id !== item.id));
      setRejectingId(null);
      setReason('');
    } catch (err) {
      // Reload rather than guess — a failed decision leaves the server
      // authoritative, and the row must not disappear on a rejected write.
      setError(err instanceof ApiError ? err.message : 'Failed to record the decision');
      void load();
    } finally {
      setBusyId(null);
    }
  };

  const takedown = async (item: QueueItem, why: string) => {
    setBusyId(item.id);
    setError(null);
    try {
      await apiPost(
        `/moderation/${encodeURIComponent(item.artifactType)}/${encodeURIComponent(item.id)}/takedown`,
        { reason: why },
      );
      setRejectingId(null);
      setReason('');
      // Reload rather than removing the row: a taken-down item stays listed, marked,
      // so the decision is visible and reversible.
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to take the content down');
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (item: QueueItem) => {
    setBusyId(item.id);
    setError(null);
    try {
      await apiPatch(
        `/moderation/${encodeURIComponent(item.artifactType)}/${encodeURIComponent(item.id)}`,
        { decision: 'approved' },
      );
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to restore the content');
    } finally {
      setBusyId(null);
    }
  };

  const reasonEditor = (item: QueueItem, onConfirm: (why: string) => void, label: string) => (
    <div className="row-actions">
      <input
        autoFocus
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (shown to the author)"
        aria-label={`Reason for ${itemTitle(item)}`}
      />
      <button
        type="button"
        className="btn btn-xs btn-primary"
        // The backend 400s without a reason, so do not offer an action that
        // cannot succeed.
        disabled={!reason.trim() || busyId === item.id}
        onClick={() => onConfirm(reason)}
      >
        {label}
      </button>
      <button
        type="button"
        className="btn btn-xs"
        onClick={() => {
          setRejectingId(null);
          setReason('');
        }}
      >
        Cancel
      </button>
    </div>
  );

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Moderation Queue</h1>
          <p className="cell-sub">
            {tab === 'review' && (
              <>
                Promotions and protocols are invisible to patients until approved here.
                {backlogDays !== null && (
                  <>
                    {' '}
                    Oldest waiting:{' '}
                    <strong>
                      {backlogDays} day{backlogDays === 1 ? '' : 's'}
                    </strong>
                    .
                  </>
                )}
              </>
            )}
            {tab === 'published' &&
              'Already live. There is no backlog here — the action is takedown, and a removal can be reversed.'}
            {tab === 'flags' &&
              'Automated image flags recorded on upload. A flag is a signal to look, not a decision.'}
          </p>
        </div>
        <div className="row-actions">
          <button
            type="button"
            className={tab === 'review' ? 'btn btn-xs btn-primary' : 'btn btn-xs'}
            aria-pressed={tab === 'review'}
            onClick={() => setTab('review')}
          >
            Awaiting review
          </button>
          <button
            type="button"
            className={tab === 'published' ? 'btn btn-xs btn-primary' : 'btn btn-xs'}
            aria-pressed={tab === 'published'}
            onClick={() => setTab('published')}
          >
            Published content
          </button>
          <button
            type="button"
            className={tab === 'flags' ? 'btn btn-xs btn-primary' : 'btn btn-xs'}
            aria-pressed={tab === 'flags'}
            onClick={() => setTab('flags')}
          >
            Upload flags
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="error-banner">
          {error}
        </div>
      )}

      {tab === 'review' && (
        <div className="row-actions" style={{ marginBottom: '0.75rem' }}>
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={s === status ? 'btn btn-xs btn-primary' : 'btn btn-xs'}
              aria-pressed={s === status}
              onClick={() => setStatus(s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {tab === 'published' && (
        <div className="row-actions" style={{ marginBottom: '0.75rem' }}>
          {PUBLISHED_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              className={t === publishedType ? 'btn btn-xs btn-primary' : 'btn btn-xs'}
              aria-pressed={t === publishedType}
              onClick={() => setPublishedType(t)}
            >
              {t === 'pulse_post' ? 'Pulse posts' : 'Specialist profiles'}
            </button>
          ))}
        </div>
      )}

      {tab === 'flags' ? (
        <DataTable
          controls={false}
          pageSize={null}
          rows={flags}
          loading={loading}
          empty="No automated flags recorded."
          getRowKey={(f: FlagItem) => f.id}
          noun="flag"
          columns={[
            { key: 'reasons', header: 'Reasons', value: (f: FlagItem) => (f.reasons ?? []).join(', ') || '—' },
            { key: 'user', header: 'Uploaded by', value: (f: FlagItem) => f.userId ?? '—' },
            { key: 'path', header: 'File', value: (f: FlagItem) => f.filePath ?? '—' },
            {
              key: 'age',
              header: 'When',
              value: (f: FlagItem) => {
                const d = ageInDays(f.createdAt);
                return d === null ? '—' : `${d}d ago`;
              },
            },
          ]}
        />
      ) : (
        <DataTable
          controls={false}
          pageSize={null}
          rows={items}
          loading={loading}
          empty={
            tab === 'published'
              ? 'No published content of this type.'
              : status === 'pending'
                ? 'Nothing awaiting review.'
                : `No ${status} items.`
          }
          getRowKey={(p: QueueItem) => `${p.artifactType}:${p.id}`}
          noun="item"
          columns={[
            {
              key: 'type',
              header: 'Type',
              value: (p: QueueItem) => p.artifactLabel ?? p.artifactType,
            },
            {
              key: 'title',
              header: 'Content',
              render: (p: QueueItem) => (
                <div>
                  <div>{itemTitle(p)}</div>
                  {(() => {
                    const ad = adPreview(p);
                    if (!ad) return null;
                    return (
                      <div className="ad-preview">
                        {ad.imageUrl && (
                          <img src={ad.imageUrl} alt={`Banner for ${itemTitle(p)}`} className="ad-preview-media" />
                        )}
                        {ad.videoUrl && (
                          <video src={ad.videoUrl} className="ad-preview-media" controls muted />
                        )}
                        {ad.description && <div className="cell-sub">{ad.description}</div>}
                        {(ad.serviceType || ad.price) && (
                          <div className="cell-sub">
                            {[ad.serviceType, ad.price].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {p.specialtyLabel && <div className="cell-sub">{p.specialtyLabel}</div>}
                  {p.takenDown && <div className="cell-sub">Taken down</div>}
                  {p.moderationReason && (
                    <div className="cell-sub">Reason: {p.moderationReason}</div>
                  )}
                </div>
              ),
            },
            {
              key: 'author',
              header: 'Author',
              value: (p: QueueItem) => p.specialistName ?? p.specialistId ?? '—',
            },
            {
              key: 'waiting',
              header: tab === 'published' ? 'Age' : 'Waiting',
              value: (p: QueueItem) => {
                const d = ageInDays(p.createdAt);
                return d === null ? '—' : `${d}d`;
              },
            },
            {
              key: 'actions',
              header: '',
              render: (p: QueueItem) => {
                if (rejectingId === `${p.artifactType}:${p.id}`) {
                  return tab === 'published'
                    ? reasonEditor(p, (why) => void takedown(p, why), 'Confirm takedown')
                    : reasonEditor(p, (why) => void decide(p, 'rejected', why), 'Confirm reject');
                }

                if (tab === 'published') {
                  return (
                    <div className="row-actions">
                      {p.takenDown ? (
                        <button
                          type="button"
                          className="btn btn-xs"
                          disabled={busyId === p.id}
                          onClick={() => void restore(p)}
                        >
                          Restore
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-xs"
                          disabled={busyId === p.id}
                          onClick={() => {
                            setRejectingId(`${p.artifactType}:${p.id}`);
                            setReason('');
                          }}
                        >
                          Take down
                        </button>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="row-actions">
                    <button
                      type="button"
                      className="btn btn-xs btn-primary"
                      disabled={busyId === p.id || p.moderationStatus === 'approved'}
                      onClick={() => void decide(p, 'approved')}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs"
                      disabled={busyId === p.id}
                      onClick={() => {
                        setRejectingId(`${p.artifactType}:${p.id}`);
                        setReason('');
                      }}
                    >
                      Reject
                    </button>
                  </div>
                );
              },
            },
          ]}
        />
      )}
    </div>
  );
}

export default ModerationQueuePage;
